import { RectangleStackIcon } from '@heroicons/react/24/outline';
import { StandardTabsV2 } from '@cimplico/components';
import { useEffect, useState } from 'react';
import { addInstance } from '../../store/slice';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentFocusField } from '../../store/selectors';
import { WorksheetSchemaList } from '../../../../../types/custom-worksheet-v2';
import { useWorkpaperContext } from '../../../../../providers/WorkpaperProvider';
import EmptyStateNew from '../../../../../components/empty-state/EmptyStateNew';
import ListItem from '../list-item/ListItem';
import FlashBackground from '../FlashBackground';
import ListItemSelect from '../list-item/ListItemSelect';
import PropertyHeader from '../PropertyHeader';
import ListSummary from './ListSummary';

type ListProps = {
    list: WorksheetSchemaList;
};

function List({ list }: ListProps): JSX.Element {
    const dispatch = useDispatch();
    const currentFocusField = useSelector(selectCurrentFocusField);

    const { readOnly } = useWorkpaperContext();

    const [tab, setTab] = useState(0);
    const [addNew, setAddNew] = useState(false);
    const [selectedListItemId, setSelectedListItemId] = useState<string | undefined>(list.properties[0]?.id);

    useEffect(() => {
        if (!currentFocusField) {
            return;
        }

        // Switch to the first tab to display template
        setTab(0);

        /**
         * If currentFocusFieldId has length of 2 (when user click on the list name in side nav instead of on specific list item), auto select the first list item
         * e.g properties.list has length of 2 -> auto select first list item within the list
         *
         * If focusedId has length greater than 2, select the list item based on focused list item id
         * e.g properties.list.properties[0] has length greater than 2
         *     -> get list item id from full property id (split at ])
         *     -> set list item id as the default value for list item select input
         */
        if (currentFocusField.id.split('.').length === 2) {
            setSelectedListItemId(`${currentFocusField.id}.properties[0]`);
        } else {
            const indexEnd = currentFocusField.id.indexOf(']') + 1;
            const listItemId = currentFocusField.id.slice(0, indexEnd);

            setSelectedListItemId(listItemId);
        }
    }, [currentFocusField, tab]);

    useEffect(() => {
        if (addNew) {
            setSelectedListItemId(list.properties[list.properties.length - 1].id);
            setAddNew(false);
        }
    }, [addNew, list]);

    function handleNewItem(): void {
        dispatch(addInstance(list.id));
        setAddNew(true);
    }

    const selectedListItem = list.properties.find((p) => p.id === selectedListItemId);

    return (
        <div id={list.id} className="scroll-mt-8 min-w-[500px] w-full relative pb-12 mb-4 border-b">
            <FlashBackground id={list.id} />
            <PropertyHeader property={list} onAdd={handleNewItem} headingSize="2xl" />
            {list.properties.length > 0 && (
                <div className="border-b dark:border-white/[0.07]">
                    <StandardTabsV2 colorMode="light" onChange={(t): void => setTab(t)} selectedIndex={tab} name={`${list.id}-tabs`}>
                        <StandardTabsV2.Tab label={`All ${list.name}`} />
                        <StandardTabsV2.Tab label="Summary" />
                    </StandardTabsV2>
                </div>
            )}
            {/* No list items */}
            {list.properties.length === 0 && (
                <div className="bg-white rounded-[5px] py-8 px-2 mt-3">
                    <EmptyStateNew
                        icon={<RectangleStackIcon />}
                        // TO-DO - Add related help link
                        helpLink="xxx"
                        helpText={`Add a new list item to "${list.name}"`}
                        title="New list item"
                        onClick={handleNewItem}
                        disableActionButton={readOnly}
                        actionText="Add list item"
                        actionButtonType="secondary"
                    />
                </div>
            )}

            {tab === 0 && list.properties.length > 0 && (
                <div>
                    <ListItemSelect
                        title={list.name}
                        listItems={list.properties}
                        value={selectedListItemId}
                        onChange={setSelectedListItemId}
                    />
                    {selectedListItem ? (
                        <ListItem key={selectedListItemId} listItem={selectedListItem} />
                    ) : (
                        <div className="bg-white rounded-[5px] py-8 px-2 mt-3">
                            <EmptyStateNew
                                icon={<RectangleStackIcon />}
                                helpText={`Please select an item from "${list.name}" to view.`}
                                title="Select list item"
                            />
                        </div>
                    )}
                </div>
            )}

            {tab === 1 && list.properties.length > 0 && <ListSummary list={list} />}
        </div>
    );
}

export default List;
